import {AddressInfoFragmentFragment, ZyusetuFieldKeyFragmentFragment,} from "@/graphql/graphql";
import {Box, Chip} from "@mui/material";
import {useCallback, useRef, useState} from "react";
import {Lightbulb, MapPin} from "lucide-react";
import {FormSchema, UpdateFieldOptions} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import {zyusetuValueIntoZodValue} from "@/hooks/useGetFirstContent";
import {useAsyncEffect} from "@/hooks/use-async-effect";

interface IsInHightideAreaProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  address: AddressInfoFragmentFragment;
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema[string],
    options?: UpdateFieldOptions
  ) => Promise<void>;
  baseLink: string;
  ls: string;
  kukaku: string;
}

export function DisportalDataViewer({
                                      address,
                                      fieldKey,
                                      updateField,
                                      baseLink,
                                      ls,
                                      kukaku,
                                    }: IsInHightideAreaProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  
  const latLngToPixel = useCallback(
    (lat: number, lng: number, zoom: number) => {
      const n = Math.pow(2, zoom);
      const lat_rad = (lat * Math.PI) / 180;
      
      const x = ((lng + 180) / 360) * n * 256;
      const y =
        ((1 - Math.log(Math.tan(lat_rad) + 1 / Math.cos(lat_rad)) / Math.PI) /
          2) *
        n *
        256;
      
      const tileX = Math.floor(x / 256);
      const tileY = Math.floor(y / 256);
      const pixelX = x - tileX * 256;
      const pixelY = y - tileY * 256;
      
      return {pixelX, pixelY};
    },
    [],
  );
  const {pixelX, pixelY} = latLngToPixel(
    address.lat,
    address.lng,
    address.zoom,
  );
  
  const [isTransparent, setIsTransparent] = useState<boolean | null>(null);
  
  const checkTransparency = useCallback(
    (src: string): Promise<number> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = src;
        
        img.onload = () => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext("2d");
          
          // キャンバスのサイズを設定
          canvas.width = img.width;
          canvas.height = img.height;
          
          // 画像を描画
          ctx?.drawImage(img, 0, 0);
          
          // ピクセルデータを取得
          const pixelData = ctx?.getImageData(
            Math.floor(pixelX),
            Math.floor(pixelY),
            1,
            1,
          ).data;
          
          // アルファ値（透明度）をチェック
          const alpha = pixelData?.[3];
          resolve(alpha as number);
        };
      });
    },
    [pixelX, pixelY],
  );
  useAsyncEffect(async () => {
    await fetch(
      `${baseLink}/${address.zoom}/${address.x}/${address.y}.png`
    ).then(
      async (response) => {
        if (response.ok) {
          
          const blob = await response.blob();
          const src = URL.createObjectURL(blob);
          setImageSrc(src);
          const check = await checkTransparency(src);
          setIsTransparent(check === 0);
          if (fieldKey.value?.value === null || fieldKey.value?.value === undefined)
            await updateField(
              fieldKey.id,
              fieldKey.keyName,
              zyusetuValueIntoZodValue(fieldKey, check !== 0),
              {hideToast: true}
            );
        } else {
          
          if (fieldKey.value?.value === null || fieldKey.value?.value === undefined)
            await updateField(
              fieldKey.id,
              fieldKey.keyName,
              zyusetuValueIntoZodValue(fieldKey, false),
              {hideToast: true}
            )
        }
      },
    )
  }, [address, baseLink]);
  
  const openHazardMap = useCallback(() => {
    const link = `https://disaportal.gsi.go.jp/hazardmap/maps/index.html?ll=${address.lat},${address.lng}&z=${address.zoom}&base=pale&ls=${ls}`;
    window.open(link, "_blank");
  }, [address.lat, address.lng, address.zoom, ls]);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  if (!imageSrc) {
    return (
      <Chip
        tabIndex={-1}
        label={`${kukaku}では有りません`}
        icon={<Lightbulb/>}
        onClick={openHazardMap}
      />
    );
  }
  return (
    <>
      {isTransparent && (
        <Chip
          tabIndex={-1}
          sx={{mb: 2}}
          label={`${kukaku}では有りません(範囲外)`}
          icon={<Lightbulb/>}
          onClick={openHazardMap}
        />
      )}
      <Box style={{position: "relative"}}>
        <canvas ref={canvasRef} style={{display: "none"}}/>
        <img src={imageSrc} style={{}} alt={"Img"}/>
        <img
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            opacity: 0.2,
            cursor: "pointer",
          }}
          onClick={openHazardMap}
          src={`https://cyberjapandata.gsi.go.jp/xyz/std/${address.zoom}/${address.x}/${address.y}.png`}
          alt={"img"}
        />
        <Box
          sx={{
            position: "absolute",
            top: pixelY,
            left: pixelX,
            transform: "translate(-50%, -50%)",
            color: "red",
          }}
        >
          <MapPin/>
        </Box>
      </Box>
    </>
  );
}
